import React, { useState, useEffect, createContext, PropsWithChildren, useContext, useRef } from 'react'
// import { useValue } from 'firebase-utils'
import { ChatSupportContextValue } from '../ChatSupport'

/** Default Five9 chat support context. */
export const Five9Context = createContext<ChatSupportContextValue | undefined>(undefined)

const FIVE9_CONTAINER_ID = 'fn-chat-iframe-container'

/**
 * Loads and initialises the Five9 client library, and providers helper functions
 */
export const Five9Provider = (props: PropsWithChildren<unknown>): JSX.Element => {
    const [isChatReady, setIsChatReady] = useState(false)
    const appendedScriptsRef = useRef<HTMLScriptElement[]>([])
    const scriptInitialisedRef = useRef(false)
    const pendingOpenRequestRef = useRef(false)

    const removeAppendedScripts = () => {
        for (const script of appendedScriptsRef.current) {
            if (document.body.contains(script)) {
                document.body.removeChild(script)
            }
        }
        appendedScriptsRef.current = []
        scriptInitialisedRef.current = false
    }

    const getChatContainer = () => document.getElementById(FIVE9_CONTAINER_ID)

    const hideChatButton = () => {
        const container = getChatContainer()
        if (container) {
            container.style.display = 'none'
        }
    }

    const showChatButton = () => {
        const container = getChatContainer()
        if (container) {
            container.style.removeProperty('display')
        }
    }

    const openChat = () => {
        showChatButton()
        if (typeof window.FIVN?.open === 'function') {
            window.FIVN.open()
            pendingOpenRequestRef.current = false
            return
        }

        pendingOpenRequestRef.current = true
    }

    useEffect(() => {
        if (scriptInitialisedRef.current) return
        scriptInitialisedRef.current = true

        const script = document.createElement('script')
        script.setAttribute('data-testid', 'five9-script')
        script.src = 'https://cdn.prod.us.five9.net/static/stable/chat/wrapper/index.js'
        let onReady = (): void => undefined
        script.onload = () => {
            const wrapper = window?.F9?.Chat?.Wrapper
            if (typeof wrapper?.init !== 'function') {
                throw new Error('Five9 not initialized properly')
            }
            onReady = () => {
                setIsChatReady(true)
                hideChatButton()
                if (pendingOpenRequestRef.current) {
                    openChat()
                }
            }
            if (wrapper.Events?.Ready && wrapper.messenger?.on) {
                wrapper.messenger.on(wrapper.Events.Ready, onReady)
            }
            wrapper.init({
                cdn: 'prod',
                integrationId: '84e336c0-10b9-4aea-b748-adf4b57b1a23',
                chatDeploymentId: '06bbb360-ae22-4a84-897a-fdbafead711b'
            })
        }
        script.onerror = (error) => {
            console.error('Failed to load Five9 chat bot', error)
        }
        script.async = true
        document.body.appendChild(script)
        appendedScriptsRef.current.push(script)

        return () => {
            const wrapper = window?.F9?.Chat?.Wrapper
            if (wrapper?.Events?.Ready && wrapper.messenger?.off) {
                wrapper.messenger.off(wrapper.Events.Ready, onReady)
            }
            wrapper?.destroy?.()
            removeAppendedScripts()
            setIsChatReady(false)
            pendingOpenRequestRef.current = false
        }
    }, [])

    return <Five9Context.Provider
        value={{
            chatReady: isChatReady,
            openChat,
            hideChatButton,
            showChatButton
        }}
    >
        {props.children}
    </Five9Context.Provider>
}

/** Returns the Five9 chat support context. */
export const useFive9Context = (): ChatSupportContextValue => {
    const five9Context = useContext(Five9Context)
    if (five9Context === undefined) {
        throw new Error('useFive9Context must be used inside Five9Provider tags')
    }

    return { ...five9Context }
}

export default Five9Provider
